/**
 * Who may use the Operator_Console and on whose behalf they act.
 *
 * - Operator roles are the roles the contract allows on the `/ops/*` operations
 *   (`x-roles` in openapi.yaml → STAFF_OPERATIONS): outlet_staff and dealer_staff.
 * - Tenant: one of the token's `tenant_ids`, sent as `X-Tenant-Id` (every ops operation
 *   requires it).
 * - Outlet: one of the token's `outlet_ids`; outlet-scoped operations (`@OutletScoped`, body
 *   `outletId`) reject other outlets with 403 and a missing one with 422 `outlet_required`.
 *
 * A stored selection is kept only while the token still carries it, else the first
 * assigned id is used. The API stays the authority; this only drives the UI.
 */
import { STAFF_OPERATIONS, type StaffRole } from '@macksons/contracts';
import { hasAnyRole, type StaffClaims } from '@macksons/web-client';

export const OPERATOR_ROLES: readonly StaffRole[] = [
  ...new Set(
    Object.values(STAFF_OPERATIONS)
      .filter((op) => op.path.startsWith('/ops/'))
      .flatMap((op) => op.roles as readonly StaffRole[]),
  ),
];

export function isOperator(claims: Pick<StaffClaims, 'roles'> | undefined): boolean {
  return hasAnyRole(claims, OPERATOR_ROLES);
}

export interface OperatorScope {
  tenantId: string | undefined;
  outletId: string | undefined;
  tenantIds: string[];
  outletIds: string[];
}

const pick = (assigned: string[], stored: string | undefined): string | undefined =>
  stored !== undefined && assigned.includes(stored) ? stored : assigned[0];

export function resolveScope(
  claims: Pick<StaffClaims, 'tenantIds' | 'outletIds'>,
  storedTenantId: string | undefined,
  storedOutletId: string | undefined,
): OperatorScope {
  return {
    tenantId: pick(claims.tenantIds, storedTenantId),
    outletId: pick(claims.outletIds, storedOutletId),
    tenantIds: claims.tenantIds,
    outletIds: claims.outletIds,
  };
}

/** Short, stable label for an id when no name is available (outlet names are admin-only). */
export function shortId(id: string): string {
  return id.replace(/-/g, '').slice(-4).toUpperCase();
}
